import { ChevronLeft, ChevronRight, Download, Loader2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { Account, Category, TxType } from "@/lib/finance";
import { monthLabel, shiftMonth } from "@/lib/format";

export type TypeFilter = "all" | TxType;
const TYPE_TABS: { value: TypeFilter; label: string }[] = [
  { value: "all", label: "Todo" }, { value: "income", label: "Ingresos" }, { value: "expense", label: "Gastos" },
  { value: "transfer", label: "Traspasos" }, { value: "adjustment", label: "Ajustes" },
];

export interface Filters { month: string; type: TypeFilter; accountId: string; categoryId: string; q: string }

interface Props {
  filters: Filters;
  onChange: (patch: Partial<Filters>) => void;
  accounts: Account[];
  categories: Category[];
  onDownload: () => void;
  downloading: boolean;
}

export function MovementsToolbar({ filters, onChange, accounts, categories, onDownload, downloading }: Props) {
  return (
    <div className="space-y-3" data-testid="movements-toolbar">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative lg:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={filters.q} onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ q: e.target.value })}
            placeholder="Buscar movimientos" className="h-11 pl-9" data-testid="movements-search-input" />
        </div>
        <div className="no-scrollbar flex gap-1 overflow-x-auto rounded-lg bg-[#F4F5F7] p-1" data-testid="movements-type-filter">
          {TYPE_TABS.map((t) => (
            <button key={t.value} onClick={() => onChange({ type: t.value })} data-testid={`filter-type-${t.value}`}
              className={`whitespace-nowrap rounded-md px-3.5 py-2 text-sm font-semibold transition-colors ${filters.type === t.value ? "bg-[#1B2A4A] text-white" : "text-slate-600 hover:text-[#1B2A4A]"}`}>
              {t.label}
            </button>
          ))}
        </div>
        <Button variant="outline" onClick={onDownload} disabled={downloading} className="h-11 border-[#DDE1E8] text-[#1B2A4A] lg:ml-auto" data-testid="download-pdf-button">
          {downloading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />} Descargar PDF
        </Button>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="flex h-11 items-center justify-between rounded-md border border-input bg-white px-1" data-testid="month-picker">
          <button onClick={() => onChange({ month: shiftMonth(filters.month, -1) })} className="rounded p-2 text-slate-500 hover:bg-slate-100" aria-label="Mes anterior" data-testid="month-prev-button"><ChevronLeft className="h-4 w-4" /></button>
          <span className="text-sm font-semibold capitalize text-[#1B2A4A]" data-testid="month-label">{monthLabel(filters.month)}</span>
          <button onClick={() => onChange({ month: shiftMonth(filters.month, 1) })} className="rounded p-2 text-slate-500 hover:bg-slate-100" aria-label="Mes siguiente" data-testid="month-next-button"><ChevronRight className="h-4 w-4" /></button>
        </div>
        <Select value={filters.accountId} onValueChange={(v: string) => onChange({ accountId: v })}>
          <SelectTrigger className="h-11 bg-white" data-testid="filter-account-select"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-72 bg-white">
            <SelectItem value="all">Todas las cuentas</SelectItem>
            {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}{a.is_archived ? " (archivada)" : ""}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.categoryId} onValueChange={(v: string) => onChange({ categoryId: v })}>
          <SelectTrigger className="h-11 bg-white" data-testid="filter-category-select"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-72 bg-white">
            <SelectItem value="all">Todas las categorías</SelectItem>
            {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
